// The disclosure RecordRow and HistoryItem share (BL-189 consolidation; Amir 26 Oct: "the same as
// History, each item expands in place"): the row's head is one button with aria-expanded and
// aria-controls, Area's chevron at its end turned down when open, and its entry is a region under it,
// hidden while it's shut. Escape inside an open row closes it and puts the focus back on its head.
// Internal: no story and no classes of its own; each row names its own, so each keeps its drawing.
import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { AreaChevron } from './Area/Area.tsx';

/** The head's ref, and the row's Escape: closes an open row and focuses its head again. */
export function useDisclosure(open: boolean, onToggle: (() => void) | undefined) {
  const head = useRef<HTMLButtonElement>(null);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || !open || !onToggle) return;
    e.stopPropagation();
    onToggle();
    head.current?.focus();
  };
  return { head, onKeyDown };
}

/** Area's chevron at the head's end, turned down when open. */
export const DisclosureChev = ({ className, open }: { className: string; open: boolean }) => (
  <span className={className}>
    <AreaChevron open={open} />
  </span>
);

/** The open row's entry, a region under its head; nothing in it while it's shut. */
export function DisclosureEntry({
  className,
  id,
  label,
  open,
  children,
}: {
  className: string;
  id: string;
  label: string | undefined;
  open: boolean;
  children: ReactNode;
}) {
  return (
    <div className={className} id={id} role="region" aria-label={label} hidden={!open}>
      {open ? children : null}
    </div>
  );
}
